import React, { useMemo, useState, useCallback } from 'react';
import { TaskItem } from '@demind-inc/core';
import clsx from 'clsx';

import './SubTasks.scss';
import { SubTaskItem } from './SubTaskItem';
import { AddSubTask } from './AddSubTask';
import { AddTodoTaskItemProps } from '../../../data-access';
import { Tooltip } from '@mui/material';

interface SubTasksProps {
  parentTask: TaskItem;
  subtasks: TaskItem[];
  canEdit?: boolean;
  onCompleteSubTask?: (taskId: string, completed: boolean) => void;
  onCreateSubTask?: (options: AddTodoTaskItemProps) => Promise<void>;
  onUpdateSubTask?: (taskId: string, updates: Partial<TaskItem>) => void;
  onDeleteSubTask?: (taskId: string) => void;
  onReorderSubTasks?: (sourceId: string, targetId: string, position: 'before' | 'after') => void;
  disableDrag?: boolean;
  className?: string;
  hideAddButton?: boolean;
  updatingSubTaskId?: string | null;
}

const SubTasks: React.FC<SubTasksProps> = ({
  parentTask,
  subtasks,
  canEdit = true,
  disableDrag = false,
  onCompleteSubTask = () => {},
  onCreateSubTask = () => Promise.resolve(),
  onUpdateSubTask = () => {},
  onDeleteSubTask = () => {},
  onReorderSubTasks = () => {},
  className,
  hideAddButton = false,
  updatingSubTaskId,
}) => {
  const [dragState, setDragState] = useState<{
    draggingTaskId: string | null;
    dragOverTargetId: string | null;
    dragOverPosition: 'before' | 'after' | null;
  }>({
    draggingTaskId: null,
    dragOverTargetId: null,
    dragOverPosition: null,
  });

  const sortedSubTasks = useMemo(() => {
    if (!subtasks.length) return [];
    return [...subtasks].sort((a, b) => {
      // First, sort by completion status (incomplete first, completed last)
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      // Then sort by childOrder within each group
      return (a.childOrder || 0) - (b.childOrder || 0);
    });
  }, [subtasks]);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, taskId: string) => {
      if (!canEdit) return;
      setDragState((prev) => ({ ...prev, draggingTaskId: taskId }));
      try {
        e.dataTransfer.setData('text/plain', taskId);
      } catch (_) {
        // noop
      }
      e.dataTransfer.effectAllowed = 'move';
    },
    [canEdit]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
      if (!dragState.draggingTaskId || dragState.draggingTaskId === targetId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const cursorY = e.clientY;
      const pos: 'before' | 'after' = cursorY < midY ? 'before' : 'after';

      if (dragState.dragOverTargetId !== targetId || dragState.dragOverPosition !== pos) {
        setDragState((prev) => ({
          ...prev,
          dragOverTargetId: targetId,
          dragOverPosition: pos,
        }));
      }
    },
    [dragState.draggingTaskId, dragState.dragOverTargetId, dragState.dragOverPosition]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
      e.preventDefault();

      const sourceId = (() => {
        try {
          return e.dataTransfer.getData('text/plain');
        } catch (_) {
          return dragState.draggingTaskId;
        }
      })();

      if (sourceId && sourceId !== targetId && onReorderSubTasks) {
        onReorderSubTasks(sourceId, targetId, dragState.dragOverPosition || 'before');
      }

      setDragState({
        draggingTaskId: null,
        dragOverTargetId: null,
        dragOverPosition: null,
      });
    },
    [dragState.draggingTaskId, dragState.dragOverPosition, onReorderSubTasks]
  );

  const handleDragEnd = useCallback(() => {
    setDragState({
      draggingTaskId: null,
      dragOverTargetId: null,
      dragOverPosition: null,
    });
  }, []);

  return (
    <div className={clsx('subtasks', className)}>
      <div className="subtasks__list">
        {sortedSubTasks.map((subtask) => (
          <Tooltip
            key={subtask.taskId}
            title={canEdit ? '' : `Please use the ${subtask.appFrom} app to edit subtasks`}
          >
            <div
              onDragOver={(e) => canEdit && !disableDrag && handleDragOver(e, subtask.taskId)}
              onDrop={(e) => canEdit && !disableDrag && handleDrop(e, subtask.taskId)}
            >
              <SubTaskItem
                task={subtask}
                onSubTaskComplete={onCompleteSubTask}
                onUpdateSubTask={onUpdateSubTask}
                onDeleteSubTask={onDeleteSubTask}
                canEdit={canEdit}
                isUpdating={updatingSubTaskId === subtask.taskId}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                isDragging={dragState.draggingTaskId === subtask.taskId}
                dragOverPosition={
                  dragState.dragOverTargetId === subtask.taskId ? dragState.dragOverPosition : null
                }
                disableDrag={disableDrag}
              />
            </div>
          </Tooltip>
        ))}
      </div>

      {canEdit && !hideAddButton && (
        <AddSubTask
          parentTask={parentTask}
          subtasksCount={sortedSubTasks.length}
          onCreateSubTask={onCreateSubTask}
          hideDragHandle
        />
      )}
    </div>
  );
};

export default SubTasks;
